<template>
  <div :class="['map-modal-overlay', { show: show }]" v-show="show" @click.self="close">
    <div class="map-modal-container" :class="{ 'modal-enter-active': show }">
      <!-- Map Header with Controls -->
      <div class="map-modal-header">
        <div class="map-header-content">
          <div class="map-title-section">
            <div class="map-icon">
              <i class="bi bi-geo-alt-fill"></i>
            </div>
            <div class="map-title-content">
              <h2 class="map-title">{{ title }}</h2>
              <div class="map-subtitle" v-if="subtitle">{{ subtitle }}</div>
            </div>
          </div>

          <!-- Map Controls -->
          <div class="map-controls">
            <!-- Search Box -->
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search location..."
                class="search-input"
                v-model="searchQuery"
                @keyup.enter="searchLocation"
                @focus="onSearchFocus"
              />
            </div>

            <!-- Control Buttons -->
            <div class="control-buttons">
              <button class="control-btn" @click="toggleMapType" :title="mapTypeTitle">
                <i class="bi" :class="mapTypeIcon"></i>
              </button>
              <button class="control-btn" @click="centerOnUser" title="My Location">
                <i class="bi bi-geo"></i>
              </button>
              <button class="control-btn" @click="toggleFullscreen" title="Fullscreen">
                <i class="bi bi-arrows-fullscreen"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Close Button -->
        <button @click="close" type="button" class="close-btn" aria-label="Close Map">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Map Body -->
      <div class="map-modal-body">
        <!-- Actual Google Maps Container -->
        <div class="map-container">
          <div ref="mapContainer" class="google-map"></div>

          <!-- Custom Map Controls Overlay -->
          <div class="map-overlay-controls">
            <!-- Zoom Controls -->
            <div class="zoom-controls">
              <button class="zoom-btn" @click="zoomIn" title="Zoom In">
                <i class="bi bi-plus-lg"></i>
              </button>
              <button class="zoom-btn" @click="zoomOut" title="Zoom Out">
                <i class="bi bi-dash-lg"></i>
              </button>
            </div>

            <!-- Compass -->
            <div class="compass-control">
              <button class="compass-btn" @click="resetNorth" title="Reset North">
                <i class="bi bi-compass"></i>
              </button>
            </div>

            <!-- Emergency Quick Actions -->
            <div class="emergency-actions" v-if="showEmergencyControls">
              <button class="emergency-btn danger" @click="addEmergencyMarker">
                <i class="bi bi-plus-circle"></i>
                Add Emergency
              </button>
              <button class="emergency-btn warning" @click="showNearbyHelp">
                <i class="bi bi-heart-pulse"></i>
                Find Help
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Footer -->
      <div class="map-modal-footer">
        <div class="footer-content">
          <!-- Coordinates Display -->
          <div class="coordinates-display">
            <i class="bi bi-geo"></i>
            <span class="coordinates"
              >Lat: {{ currentCoords.lat.toFixed(6) }}, Lng:
              {{ currentCoords.lng.toFixed(6) }}</span
            >
          </div>

          <!-- Map Status -->
          <div class="map-status">
            <div class="status-item">
              <i class="bi bi-circle-fill" :class="connectionStatus"></i>
              <span>{{ mapTypeName }}</span>
            </div>
            <div class="status-item">
              <i class="bi bi-geo-alt-fill"></i>
              <span>Zoom: {{ currentZoom }}</span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="footer-actions">
            <button class="btn btn-secondary" @click="exportMap">
              <i class="bi bi-download"></i>
              Export
            </button>
            <button class="btn btn-primary" @click="confirmLocation">
              <i class="bi bi-check-circle"></i>
              Confirm Location
            </button>
          </div>
        </div>
      </div>

      <!-- Right-click Context Menu -->
      <div
        v-if="contextMenu.show"
        class="context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="context-menu-item" @click="addMarkerAtPosition">
          <i class="bi bi-geo-alt-fill"></i>
          Add Emergency Marker Here
        </div>
        <div class="context-menu-item" @click="closeContextMenu">
          <i class="bi bi-x-circle"></i>
          Cancel
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import emergencyService from "@/services/emergencyService";
import { ref, reactive, onMounted, onUnmounted, watch, nextTick, computed } from "vue";
/* eslint-disable */
// Props
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Emergency Map View",
  },
  subtitle: {
    type: String,
    default: "Real-time emergency monitoring and management",
  },
  showEmergencyControls: {
    type: Boolean,
    default: true,
  },
  callerLocation: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: null,
  },
  callerCoordinates: {
    type: Object,
    default: () => ({ lat: 11.047606482674592, lng: 124.38233399033454 }),
  },
});

// Emits
const emit = defineEmits(["close", "confirm-location", "add-emergency", "find-help"]);
const showRouteInfo = ref(false);
const baseCoordinates = ref({ lat: 11.047606482674592, lng: 124.38233399033454 });

// Reactive data
const mapContainer = ref(null);
const searchQuery = ref("");
const currentCoords = reactive({
  lat: props.callerCoordinates.lat,
  lng: props.callerCoordinates.lng,
});
const currentZoom = ref(12);
const mapType = ref("satellite");
const isFullscreen = ref(false);
const connectionStatus = ref("connected");

// Google Maps objects
let googleMap = null;
let callerMarker = null;
let geocoder = null;

// Computed properties
const mapTypeName = computed(() => {
  const types = {
    roadmap: "Map",
    satellite: "Satellite",
    hybrid: "Hybrid",
    terrain: "Terrain",
  };
  return types[mapType.value];
});

const mapTypeIcon = computed(() => {
  const icons = {
    roadmap: "bi-map",
    satellite: "bi-globe",
    hybrid: "bi-layers",
    terrain: "bi-mountain",
  };
  return icons[mapType.value];
});

const mapTypeTitle = computed(() => {
  return `Switch to ${mapType.value === "roadmap" ? "Satellite" : "Map"} View`;
});

// Add these reactive variables
const contextMenu = reactive({
  show: false,
  x: 0,
  y: 0,
  lat: 0,
  lng: 0,
});

// Add this method to handle right-click
const handleMapRightClick = (event) => {
  if (!googleMap) return;

  // Prevent default context menu
  event.stop();

  const position = event.latLng;

  // Store position and show context menu
  contextMenu.lat = position.lat();
  contextMenu.lng = position.lng();
  contextMenu.x = event.domEvent.clientX;
  contextMenu.y = event.domEvent.clientY;
  contextMenu.show = true;
};

// Methods
const close = () => {
  clearResponderMarkers();
  emit("close");
};

let thirdMarker = null;

const addMarkerAtPosition = () => {
  if (!googleMap) return;

  const position = { lat: contextMenu.lat, lng: contextMenu.lng };
  if (thirdMarker) {
    thirdMarker.setMap(null);
    thirdMarker = null;
  }
  thirdMarker = new google.maps.Marker({
    position: position,
    map: googleMap,
    draggable: true,
    title: "Actual Emergency Location",
    icon: {
      url:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="#dc3545" stroke="#fff" stroke-width="2"/>
          <path d="M16 8L18.5 14.5L25 15L20 19.5L21.5 26L16 22.5L10.5 26L12 19.5L7 15L13.5 14.5L16 8Z" fill="#fff"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 16),
    },
  });

  thirdMarker.addListener("dragend", function (event) {
    const newPosition = event.latLng;
    const newLat = newPosition.lat();
    const newLng = newPosition.lng();
    const nposition = { lat: newLat, lng: newLng };
    moveEmergencyLocation(nposition);
    recalculateRoute(nposition);
  });

  // Emit event for parent component
  emit("add-emergency", position);

  // Recalculate route
  moveEmergencyLocation(position);
  recalculateRoute(position);

  // Hide context menu
  contextMenu.show = false;
};

const moveEmergencyLocation = (position) => {
  emergencyService.relocateEmergencyPosition(props.id, position);
};

const closeContextMenu = () => {
  contextMenu.show = false;
};

const initializeMap = () => {
  if (!window.google || !mapContainer.value) return;

  // Initialize geocoder
  geocoder = new google.maps.Geocoder();

  // Create map instance
  googleMap = new google.maps.Map(mapContainer.value, {
    center: { lat: currentCoords.lat, lng: currentCoords.lng },
    zoom: currentZoom.value,
    mapTypeId: mapType.value,
    styles: [
      {
        featureType: "all",
        elementType: "geometry",
        stylers: [{ color: "#242f3e" }],
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#242f3e" }],
      },
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ color: "#746855" }],
      },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#263c3f" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b9a76" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38414e" }],
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#212a37" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca5b3" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#746855" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1f2835" }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#f3d19c" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#17263c" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#515c6d" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#17263c" }],
      },
    ],
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
  });

  initializeDirections();
  // Add caller marker
  addCallerMarker();
  googleMap.addListener("rightclick", handleMapRightClick);
  // Add map event listeners
  googleMap.addListener("center_changed", updateCoordinates);
  googleMap.addListener("zoom_changed", updateZoom);
  googleMap.addListener("click", handleMapClick);
  setTimeout(() => {
    calculateRoute();
    fitMapToRoute();
  }, 500);
};

const addCallerMarker = () => {
  if (!googleMap) return;

  // Remove existing caller marker
  if (callerMarker) {
    callerMarker.setMap(null);
  }

  callerMarker = new google.maps.Marker({
    position: { lat: currentCoords.lat, lng: currentCoords.lng },
    map: googleMap,
    title: "Caller Location",
    icon: {
      url:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#a30e0e" stroke="#fff" stroke-width="2"/>
          <circle cx="20" cy="20" r="8" fill="#fff"/>
          <circle cx="20" cy="20" r="4" fill="#a30e0e"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 20),
    },
    animation: google.maps.Animation.BOUNCE,
  });

  // Add info window
  const infoWindow = new google.maps.InfoWindow({
    content: `
      <div style="padding: 8px;">
        <strong>Caller Location</strong><br/>
        ${props.callerLocation || "Unknown location"}
      </div>
    `,
  });

  callerMarker.addListener("click", () => {
    infoWindow.open(googleMap, callerMarker);
  });
};

const updateCoordinates = () => {
  if (!googleMap) return;
  const center = googleMap.getCenter();
  // currentCoords.lat = center.lat();
  // currentCoords.lng = center.lng();
};

const updateZoom = () => {
  if (!googleMap) return;
  currentZoom.value = googleMap.getZoom();
};
const handleMapClick = (event) => {
  console.log("Map clicked at:", event.latLng.toString());
  closeContextMenu();
};

const searchLocation = () => {
  if (!searchQuery.value.trim() || !geocoder) return;

  geocoder.geocode({ address: searchQuery.value }, (results, status) => {
    if (status === "OK" && results[0]) {
      const location = results[0].geometry.location;
      googleMap.setCenter(location);
      googleMap.setZoom(15);
    } else {
      console.error("Geocode was not successful for the following reason: " + status);
    }
  });
};

const toggleMapType = () => {
  if (!googleMap) return;
  mapType.value = mapType.value === "roadmap" ? "satellite" : "roadmap";
  googleMap.setMapTypeId(mapType.value);
};

const centerOnUser = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        googleMap.setCenter(userLocation);
        googleMap.setZoom(15);
      },
      (error) => {
        console.error("Error getting user location:", error);
        googleMap.setCenter({ lat: currentCoords.lat, lng: currentCoords.lng });
      }
    );
  }
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

const zoomIn = () => {
  if (!googleMap) return;
  googleMap.setZoom(googleMap.getZoom() + 1);
};

const zoomOut = () => {
  if (!googleMap) return;
  googleMap.setZoom(googleMap.getZoom() - 1);
};

const resetNorth = () => {
  if (!googleMap) return;
  googleMap.setHeading(0);
};

const addEmergencyMarker = () => {
  if (!googleMap) return;

  const center = googleMap.getCenter();

  const marker = new google.maps.Marker({
    position: center,
    map: googleMap,
    title: "Emergency Location",
    icon: {
      url:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="#dc3545" stroke="#fff" stroke-width="2"/>
          <path d="M16 8L18.5 14.5L25 15L20 19.5L21.5 26L16 22.5L10.5 26L12 19.5L7 15L13.5 14.5L16 8Z" fill="#fff"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 16),
    },
  });

  emit("add-emergency", { lat: center.lat(), lng: center.lng() });

  // Recalculate route if emergency marker is added
  calculateRoute();
};

const showNearbyHelp = () => {
  emit("find-help", currentCoords);
};

const onSearchFocus = () => {
  // Optional: Show search suggestions
};

const exportMap = () => {
  console.log("Exporting map...");
};

const confirmLocation = () => {
  emit("confirm-location", currentCoords);
};

// Load Google Maps script
const loadGoogleMaps = () => {
  if (window.google) {
    initializeMap();
    return;
  }

  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAJC9IxpbciCAl9qflVmDSJer2soKVsgPQ&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = initializeMap;
  document.head.appendChild(script);
};

// Watch for modal show/hide
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        loadGoogleMaps();
      });
    }
  }
);

watch(
  () => props.callerCoordinates,
  (newCoords) => {
    if (newCoords.lat && newCoords.lng) {
      currentCoords.lat = newCoords.lat;
      currentCoords.lng = newCoords.lng;
      if (googleMap) {
        googleMap.setCenter(newCoords);
        addCallerMarker();
        setTimeout(() => {
          calculateRoute();
          fitMapToRoute();
        }, 500);
      }
    }
  },
  { deep: true }
);

const travelRoutes = ref([]);
const directionsService = ref(null);
const directionsRenderer = ref(null);
const routePolyline = ref(null);

// Add this computed property
const routeInfo = ref({
  distance: "",
  duration: "",
  status: "calculating",
});

// Add this method to initialize directions services
const initializeDirections = () => {
  if (!window.google) return;

  directionsService.value = new google.maps.DirectionsService();
  directionsRenderer.value = new google.maps.DirectionsRenderer({
    map: googleMap,
    suppressMarkers: true, // We'll use custom markers
    preserveViewport: true,
    polylineOptions: {
      strokeColor: "#a30e0e",
      strokeOpacity: 0.8,
      strokeWeight: 6,
      zIndex: 1000,
    },
  });
};

const recalculateRoute = (position) => {
  if (!directionsService.value || !googleMap) return;

  const request = {
    origin: baseCoordinates.value,
    destination: { lat: position.lat, lng: position.lng },
    travelMode: google.maps.TravelMode.DRIVING,
    provideRouteAlternatives: true, // Get multiple routes
    drivingOptions: {
      departureTime: new Date(),
      trafficModel: google.maps.TrafficModel.BEST_GUESS,
    },
  };

  routeInfo.value.status = "calculating";

  directionsService.value.route(request, (result, status) => {
    if (status === "OK") {
      displayRoutes(result);
    } else {
      console.error("Directions request failed: " + status);
      routeInfo.value.status = "error";
      // Fallback: Draw straight line if directions fail
      drawStraightLineRoute();
    }
  });
};

// Add this method to calculate and display route
const calculateRoute = () => {
  if (!directionsService.value || !googleMap) return;

  const request = {
    origin: baseCoordinates.value,
    destination: { lat: currentCoords.lat, lng: currentCoords.lng },
    travelMode: google.maps.TravelMode.DRIVING,
    provideRouteAlternatives: true, // Get multiple routes
    drivingOptions: {
      departureTime: new Date(),
      trafficModel: google.maps.TrafficModel.BEST_GUESS,
    },
  };

  routeInfo.value.status = "calculating";

  directionsService.value.route(request, (result, status) => {
    if (status === "OK") {
      displayRoutes(result);
    } else {
      console.error("Directions request failed: " + status);
      routeInfo.value.status = "error";
      // Fallback: Draw straight line if directions fail
      drawStraightLineRoute();
    }
  });
};

// Enhanced route display with multiple route options
const displayRoutes = (result) => {
  if (!directionsRenderer.value) return;

  // Clear previous routes
  clearRoutes();

  // Display the first route (fastest) by default
  directionsRenderer.value.setDirections(result);

  // Store all routes for potential selection
  travelRoutes.value = result.routes;

  // Update route info for the primary route
  const primaryRoute = result.routes[0];
  const leg = primaryRoute.legs[0];

  routeInfo.value = {
    distance: leg.distance.text,
    duration: leg.duration.text,
    status: "calculated",
    routesAvailable: result.routes.length,
  };

  // Add custom markers for start and end points
  addRouteMarkers();

  // Optional: Show route alternatives as faint lines
  showRouteAlternatives(result.routes);
};

// Draw straight line as fallback
const drawStraightLineRoute = () => {
  if (!googleMap) return;

  routePolyline.value = new google.maps.Polyline({
    path: [baseCoordinates.value, { lat: currentCoords.lat, lng: currentCoords.lng }],
    geodesic: true,
    strokeColor: "#a30e0e",
    strokeOpacity: 0.7,
    strokeWeight: 4,
    strokeDashArray: [10, 5], // Dashed line for estimated route
    map: googleMap,
  });

  routeInfo.value = {
    distance: "Calculating...",
    duration: "Estimated",
    status: "estimated",
  };

  addRouteMarkers();
};

// Add custom markers for route start and end
const addRouteMarkers = () => {
  // Base marker (green)
  new google.maps.Marker({
    position: baseCoordinates.value,
    map: googleMap,
    title: "Base Operations",
    icon: {
      url:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="16" cy="16" r="14" fill="#28a745" stroke="#fff" stroke-width="2"/>
          <path d="M16 8L20 12L24 8L22 16L28 18L22 20L24 28L20 24L16 28L12 24L8 28L10 20L4 18L10 16L8 8L12 12L16 8Z" fill="#fff"/>
        </svg>
      `),
      scaledSize: new google.maps.Size(32, 32),
      anchor: new google.maps.Point(16, 16),
    },
  });

  // Caller marker (red) - if not already added
  if (!callerMarker) {
    callerMarker = new google.maps.Marker({
      position: { lat: currentCoords.lat, lng: currentCoords.lng },
      map: googleMap,
      title: "Caller Location",
      icon: {
        url:
          "data:image/svg+xml;base64," +
          btoa(`
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="18" fill="#a30e0e" stroke="#fff" stroke-width="2"/>
            <circle cx="20" cy="20" r="8" fill="#fff"/>
            <circle cx="20" cy="20" r="4" fill="#a30e0e"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
      animation: google.maps.Animation.BOUNCE,
    });
  }
};

// Show alternative routes as faint lines
const showRouteAlternatives = (routes) => {
  if (routes.length <= 1) return;

  // Skip first route (already displayed as primary)
  for (let i = 1; i < routes.length; i++) {
    const alternativeRoute = new google.maps.Polyline({
      path: routes[i].overview_path,
      geodesic: true,
      strokeColor: "#6c757d",
      strokeOpacity: 0.4,
      strokeWeight: 3,
      map: googleMap,
      zIndex: 500,
    });

    travelRoutes.value[i].polyline = alternativeRoute;
  }
};

// Clear all routes
const clearRoutes = () => {
  if (directionsRenderer.value) {
    directionsRenderer.value.setDirections({ routes: [] });
  }

  if (routePolyline.value) {
    routePolyline.value.setMap(null);
    routePolyline.value = null;
  }

  // Clear alternative routes
  travelRoutes.value.forEach((route, index) => {
    if (index > 0 && route.polyline) {
      route.polyline.setMap(null);
    }
  });

  travelRoutes.value = [];
};

// Add this method to fit map to show both points
const fitMapToRoute = () => {
  if (!googleMap) return;

  const bounds = new google.maps.LatLngBounds();
  bounds.extend(baseCoordinates.value);
  bounds.extend({ lat: currentCoords.lat, lng: currentCoords.lng });

  googleMap.fitBounds(bounds);

  // Add some padding
  googleMap.panBy(0, -50);
};
// Store markers in a Map for easy access by userId
const userMarkers = new Map();

const handleResponderLocationUpdate = (event) => {
  if (!googleMap) return; //if map is not yet loaded then no need to process
  const userLocationData = event.detail;
  console.log(userLocationData);
  createOrUpdatePinOnMap(userLocationData);
};

const clearResponderMarkers = () => {
  userMarkers.forEach((marker, userId) => {
    marker.setMap(null); // Remove from map
  });
  userMarkers.clear(); // Clear the Map
  console.log("All markers cleared from map");
};

const createOrUpdatePinOnMap = (userLocationData) => {
  const { userId, latitude, longitude } = userLocationData;

  // Check if marker already exists for this user
  if (userMarkers.has(userId)) {
    // Update existing marker position smoothly
    updateMarkerPosition(userId, latitude, longitude);
  } else {
    // Create new marker
    createNewMarker(userId, latitude, longitude);
  }
};

const createNewMarker = (userId, lat, lng) => {
  const markerIcon = {
    url: require("@/assets/markers/pert_marker.png"), // Vue require syntax
    scaledSize: new google.maps.Size(60, 60), // Adjust size as needed
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(20, 60), // Center bottom for pin-like behavior
  };

  const marker = new google.maps.Marker({
    position: { lat, lng },
    map: googleMap,
    title: `User ${userId}`,
    // Optional: Add custom icon
    icon: markerIcon,
    animation: google.maps.Animation.DROP,
  });

  // Store marker reference
  userMarkers.set(userId, marker);

  console.log(`Created new marker for user ${userId}`);
};

const updateMarkerPosition = (userId, newLat, newLng) => {
  const marker = userMarkers.get(userId);

  if (!marker) return;

  const oldPosition = marker.getPosition();
  const newPosition = new google.maps.LatLng(newLat, newLng);

  // Check if position actually changed
  if (oldPosition.equals(newPosition)) {
    return;
  }

  // Smooth animation using setInterval for gradual movement
  animateMarker(marker, oldPosition, newPosition, 1000); // 1000ms animation duration
};

// Smooth animation function
const animateMarker = (marker, startPosition, endPosition, duration) => {
  const startTime = Date.now();
  const startLat = startPosition.lat();
  const startLng = startPosition.lng();
  const deltaLat = endPosition.lat() - startLat;
  const deltaLng = endPosition.lng() - startLng;

  // Remove any existing animation
  if (marker.animationTimeout) {
    clearTimeout(marker.animationTimeout);
  }

  const animateStep = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function for smooth movement (ease-in-out)
    const easeProgress = easeInOutQuad(progress);

    const currentLat = startLat + deltaLat * easeProgress;
    const currentLng = startLng + deltaLng * easeProgress;

    marker.setPosition({
      lat: currentLat,
      lng: currentLng,
    });

    if (progress < 1) {
      marker.animationTimeout = setTimeout(animateStep, 16); // ~60fps
    } else {
      // Animation complete
      delete marker.animationTimeout;
    }
  };

  animateStep();
};

// Easing function for smooth animation
const easeInOutQuad = (t) => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

// Alternative: Simple instant update without animation
const updateMarkerPositionInstant = (userId, lat, lng) => {
  const marker = userMarkers.get(userId);
  if (marker) {
    marker.setPosition({ lat, lng });
  }
};

// Optional: Remove marker when user is no longer active
const removeUserMarker = (userId) => {
  const marker = userMarkers.get(userId);
  if (marker) {
    marker.setMap(null);
    userMarkers.delete(userId);
    console.log(`Removed marker for user ${userId}`);
  }
};

// Optional: Update multiple users at once
// const updateMultipleUserLocations = (usersLocationData) => {
//   usersLocationData.forEach((userLocationData) => {
//     createOrUpdatePinOnMap(userLocationData);
//   });
// };

onMounted(() => {
  window.addEventListener("responderLocationUpdated", handleResponderLocationUpdate);
  if (props.show) {
    loadGoogleMaps();
  }
});

onUnmounted(() => {
  clearRoutes();
  if (callerMarker) {
    callerMarker.setMap(null);
  }
  clearResponderMarkers();
  window.removeEventListener("responderLocationUpdated", handleResponderLocationUpdate);
});

defineExpose({
  clearResponderMarkers,
});
</script>

<style scoped>
/* Context Menu Styles */
.context-menu {
  position: fixed;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  padding: 4px 0;
  z-index: 10000;
  min-width: 200px;
  border: 1px solid #e0e0e0;
}

.context-menu-item {
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  transition: background-color 0.2s;
}

.context-menu-item:hover {
  background-color: #f5f5f5;
}

.context-menu-item i {
  color: #a30e0e;
  font-size: 14px;
  width: 16px;
}

/* Map Modal Overlay */
.map-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  z-index: 9999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.map-modal-overlay.show {
  opacity: 1;
  visibility: visible;
}

/* Map Modal Container */
.map-modal-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.95);
  width: 95vw;
  height: 90vh;
  max-width: 1400px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 100px rgba(163, 14, 14, 0.2);
  z-index: 10000;
  opacity: 0;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.map-modal-container.modal-enter-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Map Header - FIXED: Ensure it stays on top */
.map-modal-header {
  background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 100;
  /* Increased z-index */
  flex-shrink: 0;
}

.map-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.map-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.map-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

.map-icon i {
  font-size: 1.5rem;
  color: white;
}

.map-title-content {
  color: white;
}

.map-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.map-subtitle {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Map Controls */
.map-controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
  max-width: 600px;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 300px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.6);
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #a30e0e;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 2px rgba(163, 14, 14, 0.3);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.control-buttons {
  display: flex;
  gap: 0.5rem;
}

.control-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  z-index: 101;
  /* Ensure close button is on top */
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1) rotate(90deg);
}

/* Map Body - FIXED: Proper container for map */
.map-modal-body {
  flex: 1;
  position: relative;
  background: #0a0a0a;
  min-height: 0;
  /* Important for flexbox */
  overflow: hidden;
}

.map-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0a0a0a;
}

.google-map {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

/* Map Overlay Controls - FIXED: Ensure they stay on top of map */
.map-overlay-controls {
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 90;
  /* Lower than header but higher than map */
}

.zoom-controls {
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.zoom-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.zoom-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.zoom-btn:first-child {
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.compass-control {
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.compass-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
}

.compass-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(15deg);
}

.emergency-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.emergency-btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(10px);
  white-space: nowrap;
}

.emergency-btn.danger {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
}

.emergency-btn.warning {
  background: linear-gradient(135deg, #e67700 0%, #cc6a00 100%);
}

.emergency-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

/* Map Footer - FIXED: Ensure it stays on top */
.map-modal-footer {
  background: rgba(0, 0, 0, 0.9);
  padding: 1.25rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 100;
  /* Same as header */
  flex-shrink: 0;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.coordinates-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
}

.coordinates-display i {
  color: #a30e0e;
}

.map-status {
  display: flex;
  gap: 1.5rem;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
}

.status-item i.connected {
  color: #28a745;
  animation: pulse 2s infinite;
}

.footer-actions {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(163, 14, 14, 0.4);
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .map-modal-container {
    width: 98vw;
    height: 95vh;
  }

  .map-header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .map-controls {
    max-width: none;
  }

  .footer-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .map-status {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .map-modal-header {
    padding: 1rem 1.5rem;
  }

  .map-modal-footer {
    padding: 1rem 1.5rem;
  }

  .map-overlay-controls {
    bottom: 1rem;
    right: 1rem;
  }

  .emergency-actions {
    flex-direction: row;
  }

  .emergency-btn {
    font-size: 0.7rem;
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .map-title-section {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }

  .map-controls {
    flex-direction: column;
    gap: 1rem;
  }

  .search-box {
    max-width: none;
  }

  .control-buttons {
    justify-content: center;
  }

  .footer-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn {
    justify-content: center;
  }
}
</style>
